import { useSoftDeleteCommercialEvaluation } from '@src/main/modules/reports/commercial/application/hook/useDeleteCommercialEvaluation';
import { useDownloadCommercialEvaluationPdf } from '@src/main/modules/reports/commercial/application/hook/useDownloadCommercialEvaluation';
import { BriefCommercialEvaluation } from '@src/main/modules/reports/shared/domain/evaluation.interface';
import { getTimestamp } from '@src/main/shared/utils/dateUtils';
import { logger } from '@src/main/shared/utils/logger';
import saveAs from 'file-saver';
import { useCallback, useState } from 'react';

export const useCommercialEvaluationActions = () => {
  const { mutateAsync: downloadPdfMutation } = useDownloadCommercialEvaluationPdf();
  const { mutateAsync: deleteEvaluationMutation } = useSoftDeleteCommercialEvaluation();

  const [evaluationToDelete, setEvaluationToDelete] = useState<BriefCommercialEvaluation | null>(
    null,
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const downloadPdf = useCallback(
    async (evaluation: BriefCommercialEvaluation) => {
      try {
        const blob = await downloadPdfMutation(evaluation.id);
        // eslint-disable-next-line max-len
        const fileName = `commercial_evaluation_${evaluation.carrierCode}${evaluation.flightNumber}_${evaluation.flightDate}_${evaluation.departureAirport}_${evaluation.arrivalAirport}_${getTimestamp()}.pdf`;
        saveAs(blob, fileName);
      } catch (error) {
        logger.error('Error downloading PDF', error);
      }
    },
    [downloadPdfMutation],
  );

  const handleDeleteEvaluation = useCallback((evaluation: BriefCommercialEvaluation) => {
    setEvaluationToDelete(evaluation);
    setIsDeleteModalVisible(true);
  }, []);

  const confirmDeleteEvaluation = useCallback(
    async (onAfterDelete?: () => void) => {
      if (!evaluationToDelete) return;
      try {
        await deleteEvaluationMutation(evaluationToDelete.id);
        onAfterDelete?.();
      } catch (error) {
        logger.error('Error deleting evaluation', error);
      } finally {
        setEvaluationToDelete(null);
        setIsDeleteModalVisible(false);
      }
    },
    [evaluationToDelete, deleteEvaluationMutation],
  );

  const rejectDeleteEvaluation = useCallback(() => {
    setEvaluationToDelete(null);
    setIsDeleteModalVisible(false);
  }, []);

  return {
    downloadPdf,
    handleDeleteEvaluation,
    confirmDeleteEvaluation,
    rejectDeleteEvaluation,
    evaluationToDelete,
    isDeleteModalVisible,
    setIsDeleteModalVisible,
  };
};
